<template>
  <div class="elevation-chart-wrap">
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
    <canvas
      ref="canvasRef"
      class="elevation-chart"
      @mouseleave="cleanUpToolTip"
    />
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import { ref, onMounted, reactive, onUnmounted } from 'vue'
import min from 'lodash/min'
import max from 'lodash/max'
import {
  Chart,
  LineController,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler,
  Tooltip,
} from 'chart.js'
import { getRelativePosition } from 'chart.js/helpers'
import length from '@turf/length'
import along from '@turf/along'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import elevationLowestPointIcon from '@/assets/icons/elevation_lowest_point.svg'
import elevationHighestPointIcon from '@/assets/icons/elevation_highest_point.svg'
import {
  DEFAULT_UNIT_TYPE,
  metersToMiles,
  metersToFeet,
  metersToKilometers,
} from '@/components/shared/x-distance.vue'

import { setElevationScrubMarker } from '@/services/map-service'
import { getItem } from '@/plugins/storage'
import markupService from '@/services/markup-service'
import { log } from '@/services/logging-service'

import { LINES } from '@/lib/types'
import { commaSeparatedNumber } from '@/lib/math'
import { convertGeobufToJSON } from '@/lib/feature-utils'
import { slopeToColor } from '@/lib/elevation-graph-utils'

/**
 * Define scrubbing vertical line label alignment based on scrub position:
 *
 * - If position is in the first 10% of the chart, then align to the left (So the text body sticks out to the right);
 * - If position is in the last 10% of the chart, then align to the right (So the text body sticks out to the left);
 * - Otherwise, center it;
 *
 * @param index Label index
 * @param length Data length
 * @returns CSS text-align property value
 * */
function align(index, length) {
  const ratio = index / length
  if (ratio < 0.1) {
    return 'left'
  } else if (ratio > 0.9) {
    return 'right'
  }
  return 'center'
}

function getElevationUnit(isImperial) {
  return isImperial ? 'ft' : 'm'
}
function getDistanceUnit(isImperial) {
  return isImperial ? 'mi' : 'km'
}
function convertElevation(value, isImperial) {
  return isImperial ? metersToFeet(value) : value
}

function convertDistance(value, isImperial) {
  return isImperial ? metersToMiles(value) : metersToKilometers(value)
}

async function generateElevationGeoJson(elevationPointProgress) {
  if (!elevationPointProgress) {
    // If all values are null, clear data
    return null
  }

  const {
    xIndex,
    currentElevation,
    elevationUnit,
    geobufString,
    routeGeoJson,
    convertedDistanceVal,
    unit,
    totalDataPoints,
  } = elevationPointProgress
  // Placeholder to populate depending on whether we have markup or rich content
  let geojson = null
  // If there is no geobuf string, we're working with user markups
  if (routeGeoJson) {
    geojson = routeGeoJson
  } else if (!geobufString) {
    // Get the geojson from the markup service
    const { markup } = markupService.getFocusedMarkup()

    geojson = markup?.toGeoJSON()
  } else {
    // If there is a geobuf string, we're working with rich content
    // Convert the geobuf string to usable geojson
    const data = await convertGeobufToJSON(geobufString)
    geojson = data?.features?.[0]
  }

  // Measure the length of our line via turf
  try {
    const lineLength = length(geojson)
    // Find where on line we should be via our progress point
    const progress = lineLength * (xIndex / (totalDataPoints - 1))
    // Generate a Point along the line at that point via turf
    const displayPoint = along(geojson, progress)
    // Add properties to Feature with elevation point data
    displayPoint.properties.id = 'elevation-point-label'
    displayPoint.properties.elevation_point_number = currentElevation
    displayPoint.properties.elevation_point_unit = elevationUnit
    displayPoint.properties.distance_point_number = convertedDistanceVal
    displayPoint.properties.distance_point_unit = ' ' + unit
    displayPoint.properties.tooltip_pill = 'tooltip_card'
    // Add the Point to the map
    return [displayPoint]
  } catch (e) {
    log('line_length_calculation_failed', {
      geo_json: geojson,
      error: e,
    })
  }
}

/**
 * Extension of default ChartJS Line Class
 * Used to append custom canvas tooltip to Chart on drawing frames
 */
class LineWithLine extends LineController {
  async draw() {
    super.draw(arguments)

    const { ctx } = this.chart
    // Check if tooltip event is active
    if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
      const meta = this.getMeta()
      const { data } = meta
      const { xPos, xIndex } = this.chart.mouseInfo
      const totalDataPoints = data.length
      const currIndex = data[xIndex]
      const elevationData = currIndex?.$context.raw
      // Only create tooltip if we're on a data point with elevation
      if (xIndex >= 0 && xIndex <= data.length && elevationData) {
        const { distance, isImperial, geobufString, routeGeoJson } =
          this.chart.options
        const convertedDistance = convertDistance(distance, isImperial)
        const progressPercent = xIndex / (totalDataPoints - 1)
        const convertedDistanceVal = (
          convertedDistance * progressPercent
        ).toFixed(1)

        const unit = getDistanceUnit(isImperial)
        const label = `${convertedDistanceVal} ${unit}`
        // Format data for scrubbing point label
        const currentElevation = commaSeparatedNumber(
          Math.floor(convertElevation(elevationData, isImperial))
        )
        const elevationUnit = ` ${getElevationUnit(isImperial)}`

        ctx.save()
        // Tooltip line
        ctx.beginPath()
        ctx.moveTo(xPos, 17)
        ctx.lineTo(xPos, this.chart.chartArea.bottom)
        ctx.lineWidth = 2
        ctx.strokeStyle = 'black'
        ctx.fillStyle = 'black'
        ctx.stroke()
        // Tooltip label
        ctx.font = 'bold 12px Roboto'
        ctx.textAlign = align(xIndex, data.length)
        ctx.fillText(label, xPos, 13)
        ctx.closePath()
        // Tooltip dot
        ctx.fillStyle = 'black'
        ctx.arc(xPos, currIndex.y, 4, 0, 2 * Math.PI)
        ctx.fill()
        ctx.restore()
        // Set data to display scrubbing point on map
        const geoJson = await generateElevationGeoJson({
          xIndex,
          convertedDistanceVal,
          unit,
          currentElevation,
          elevationUnit,
          geobufString,
          routeGeoJson,
          totalDataPoints,
        })
        elevationScrubber.value = geoJson
      }
    }
  }
}

LineWithLine.id = 'LineWithLine'
LineWithLine.defaults = LineController.defaults

const elevationScrubber = ref([])

export default {
  name: 'XElevationProfile',
  props: {
    points: {
      type: Array,
      required: true,
    },
    distance: {
      type: Number,
      required: true,
    },
    lineStyle: {
      type: String,
      default: LINES.SOLID,
    },
    geobufString: {
      type: String,
      default: '',
    },
    // eslint-disable-next-line vue/no-unused-properties
    gain: {
      type: Number,
      required: true,
    },
    routeGeoJson: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const unitType = getItem('unitType')
    // Return these for access in Vue component
    const canvasRef = ref(null)
    const isImperial = unitType === DEFAULT_UNIT_TYPE
    //0 = solid line, 2 = dotted line, 10 = dashed line
    const getBorderDash = () => {
      if (
        props.lineStyle === LINES.SOLID ||
        props.lineStyle === LINES.GRADIENT
      ) {
        return [0]
      } else if (props.lineStyle === LINES.DOTS) {
        return [2]
      }
      return [10]
    }

    const pMin = min(props.points)
    const pMax = max(props.points)
    let lineLowestPoint = null
    let lineHighestPoint = null
    // Arrays to hold point styles for line above chart
    const pBackgroundColor = []
    const pStyle = []
    const pBorderColor = []
    const pRadius = []

    onMounted(() => {
      const canvas = canvasRef.value
      const ctx = canvas.getContext('2d')
      const graphGrayColor = '#BDBDBD'
      canvas.ariaLabel = 'Elevation profile graph'
      canvas.role = 'img'
      const state = reactive({
        gradientColor: ctx.createLinearGradient(0, 0, canvas.width, 0),
        _chart: null,
      })

      // Icons for high/low points on chart
      const LPIcon = new Image()
      LPIcon.src = elevationLowestPointIcon
      const HPIcon = new Image()
      HPIcon.src = elevationHighestPointIcon

      const hasGradient = props.lineStyle === 'gradient'
      // Use gradient color line for Rich Content, otherwise use markup line color
      const getBorderColor = hasGradient ? state.gradientColor : graphGrayColor
      props.points.forEach((v, i, points) => {
        // Display 4 points with appropriate icons:
        // First point in data, last point, highest point, lowest point
        pBackgroundColor[i] = 'transparent'
        pBorderColor[i] = 'transparent'
        pRadius[i] = 6

        // Style first/last points
        if (i === 0 || i === props.points.length - 1) {
          pStyle[i] = 'round'
          pBackgroundColor[i] = 'white'
          pBorderColor[i] = graphGrayColor
          pRadius[i] = 4
        }

        // Get lowest point in data
        if (v === pMin && lineLowestPoint === null) {
          pStyle[i] = LPIcon
          lineLowestPoint = i
        }

        // Get highest point in data
        if (v === pMax && lineHighestPoint === null) {
          pStyle[i] = HPIcon
          lineHighestPoint = i
        }

        // Generate gradient color stops
        // Used as chart background, and as line style for Rich Content
        if (i !== points.length - 1) {
          const endY = points[i + 1]
          const step = props.distance / points.length
          const slopeColor = slopeToColor(endY, v, step)
          const ratio = (i + 1) / points.length
          state.gradientColor.addColorStop(
            ratio,
            `hsla(${slopeColor.join(',')}, .5)`
          )
        }
      })

      // these positions are clockwise degrees with 0 being directly to the right
      const LABEL_POSITION = {
        RIGHT_OFFSET: 315,
        LEFT_OFFSET: 240,
        // use 270 instead of 'center' so that the offset value will work
        CENTER: 270,
      }

      const data = {
        labels: props.points,
        // Chart displays data as three data sets:
        datasets: [
          // One for the border line
          {
            borderColor: getBorderColor,
            borderDash: getBorderDash(),
            borderWidth: 2,
            pointRadius: pRadius,
            pointStyle: pStyle,
            pointBorderColor: pBorderColor,
            pointBackgroundColor: pBackgroundColor,
            fill: false,
            data: props.points,
            datalabels: {
              labels: {
                // Format, align, and display the High & Low elevation labels
                value: {
                  align: (ctx) => {
                    const ratio = ctx.dataIndex / ctx.dataset.data.length
                    if (ratio < 0.1) {
                      // near the end of the chart
                      return LABEL_POSITION.RIGHT_OFFSET
                    } else if (ratio > 0.9) {
                      // near the beginning of the char
                      return LABEL_POSITION.LEFT_OFFSET
                    }
                    // middle
                    return LABEL_POSITION.CENTER
                  },
                  offset: 9,
                  display: (ctx) => {
                    return (
                      ctx.dataIndex === lineLowestPoint ||
                      ctx.dataIndex === lineHighestPoint
                    )
                  },
                  formatter: (value) => {
                    const num = commaSeparatedNumber(
                      Math.floor(convertElevation(value, isImperial))
                    )
                    return `${num} ${getElevationUnit(isImperial)}`
                  },

                  color: '#222222',
                  backgroundColor: '#FFF',
                  padding: {
                    top: 0,
                    right: 2,
                    bottom: 0,
                    left: 2,
                  },
                  font: {
                    family: 'Roboto',
                    weight: 'bolder',
                  },
                },
              },
            },
          },
          // One white line mask to act as the gap between gradient and border
          {
            borderColor: '#fff',
            borderDash: [0],
            borderWidth: 10,
            pointRadius: 0,
            pointHoverRadius: 0,
            fill: false,
            data: props.points,
          },
          // Another for the gradient points
          {
            pointRadius: 0,
            pointHoverRadius: 0,
            data: props.points,
            backgroundColor: state.gradientColor,
            fill: 'start',
            // Hide border on top of gradient points line
            borderColor: 'rgba(0, 0, 0, 0)',
          },
        ],
      }

      const options = {
        scales: {
          y: {
            display: false,
            // Helps to scale data within the canvas bounds to leave room for labels.
            // The best we can tell, this percentage is of the y-axis low-to-high value
            // so a chart with low of 4000 ft and high of 5000ft will add a 1000 ft
            // "buffer" to the top and bottom of the chart with a grace value of 100%.
            // However, this is just a guess at what chart.js is doing.
            grace: '100%',
            gridLines: {
              display: false,
            },
            ticks: {
              beginAtZero: true,
            },
          },

          x: {
            offset: true,
            display: false,
            ticks: {
              beginAtZero: true,
            },
            gridLines: {
              display: true,
            },
          },
        },
        responsive: false,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 5,
            right: 8,
            top: 10,
          },
        },
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
          },
          interaction: {
            intersect: false,
            mode: 'index',
          },
        },
        // Bind additional functions to the Chart to access Vue instance
        distance: props.distance,
        geobufString: props.geobufString,
        isImperial: isImperial,
        routeGeoJson: props.routeGeoJson,
      }

      Chart.register(
        LineController,
        LineElement,
        PointElement,
        LinearScale,
        CategoryScale,
        Filler,
        Tooltip,
        ChartDataLabels,
        LineWithLine
      )

      state._chart = new Chart(ctx, {
        type: 'LineWithLine',
        data,
        options: {
          ...options,
          onHover: (e) => {
            // Get mouse location on canvas
            const canvasPosition = getRelativePosition(e, state._chart)
            // Get data index at mouse location
            const dataX = state._chart.scales.x.getValueForPixel(
              canvasPosition.x
            )
            state._chart.mouseInfo = {
              xPos: canvasPosition['x'],
              xIndex: dataX,
            }
          },
        },
        plugins: [
          {
            beforeEvent(chart, args) {
              const event = args.event
              // Hide scrub marker when leaving chart
              if (event.type === 'mouseout') {
                elevationScrubber.value = []
              }
            },
          },
        ],
      })

      // set the elevation scrubber to the local reactive value
      setElevationScrubMarker(elevationScrubber)
    })

    onUnmounted(() => {
      elevationScrubber.value = []
    })

    /**
     * This clean is needed because in some cases the tooltip and dot are not
     * getting cleared from the map even though the shared observable state has
     * been set. Waiting a couple milliseconds and then retriggering the clear
     * resolves the issue, but it is unclear why Mapbox is not picking up the
     * original change from mouseout on the chart above.
     */
    function cleanUpToolTip() {
      setTimeout(() => {
        elevationScrubber.value = []
      }, 60)
    }

    // eslint-disable-next-line vue/no-unused-properties
    return { canvasRef, isImperial, cleanUpToolTip }
  },
}
</script>

<style scoped>
.elevation-chart {
  width: 100%;
  height: 190px;
}

.elevation-chart-wrap {
  position: relative;
}
</style>
